import React, { useMemo } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import logo from './logo_unofficial.png'; // Import your image file
import jtiLogoWhite from './jti_logo_white_transparent.png';
import batLogoWhite from './bat_logo_white_transparent.png';
import pmiLogo from './pmi_logo.png';
import forloopLogoWhite from './forloop_logo_white_transparent.svg';
import { Link } from 'react-router-dom'; // Import BrowserRouter, Route, Switch, Link
import ProfileBubble from './ProfileBubble';
import NotificationsBell from './NotificationsBell';

const NavigationBar = ({
  isLoggedIn,
  projectKey,
  scopeMode,
  onScopeChange,
  marketOptions = [],
  selectedMarket,
  onMarketChange,
  uiStyle
}) => {
  const isJtiGlobal = projectKey === 'jti-global';
  const isJtiProject = ['jti-global', 'jti', 'jti-sweden'].includes(projectKey);
  const isBatProject = ['bat', 'pmi'].includes(projectKey);
  const isPmiProject = projectKey === 'pmi';
  const isJtiScopeProject = ['jti-global', 'jti'].includes(projectKey);
  const showMarketSelector = isJtiScopeProject && scopeMode === 'market';
  const isWhiteLabel = uiStyle === 'white-label';

  // BAT / PMI white-label header colors
  const batHeaderBlue = '#24336B';
  const pmiHeaderBlue = 'rgb(0, 116, 194)';
  const clientHeaderBlue = isPmiProject ? pmiHeaderBlue : batHeaderBlue;

  /** Aligns HQ/Market toggle + Market select ripples/menus with page accent (not default MUI blue). Matches JTIPriceAndPromotionsView STYLE_PRESETS. */
  const jtiClassicAccent = '#2e7d32';
  const jtiWhiteLabelAccent = '#3d8f55';
  const filterAccent = useMemo(() => {
    if (isWhiteLabel && isPmiProject) return pmiHeaderBlue;
    if (isWhiteLabel && isBatProject) return batHeaderBlue;
    if (isWhiteLabel && isJtiProject) return jtiWhiteLabelAccent;
    if (isJtiScopeProject) return jtiClassicAccent;
    return jtiClassicAccent;
  }, [isWhiteLabel, isPmiProject, isBatProject, isJtiProject, isJtiScopeProject]);

  const navFilterTheme = useMemo(
    () =>
      createTheme({
        palette: {
          primary: {
            main: filterAccent,
            contrastText: '#fff',
          },
        },
      }),
    [filterAccent]
  );
  const headerBackground = isWhiteLabel && isBatProject ? clientHeaderBlue : (isWhiteLabel ? '#2f8f4e' : 'black');
  const showJtiBranding = isJtiGlobal || (isWhiteLabel && isJtiProject);
  const showBatBranding = isWhiteLabel && isBatProject;
  const clientBrandLogoSrc = isPmiProject ? pmiLogo : batLogoWhite;
  const clientBrandLogoAlt = isPmiProject ? 'PMI' : 'BAT';
  const showBranding = showJtiBranding || showBatBranding;

  return (
    <AppBar position="fixed" sx={{ backgroundColor: headerBackground, height: '56px', zIndex: 1201 }}>
      <Toolbar
        sx={{
          minHeight: '56px !important',
          height: '56px',
          padding: '0 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 200 }}>
          <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>

              {/* Client brand logo (BAT / JTI) — only when branding applies */}
              {showBranding && (
                <img
                  src={showBatBranding ? clientBrandLogoSrc : jtiLogoWhite}
                  alt={showBatBranding ? clientBrandLogoAlt : 'JTI'}
                  style={{ height: 34 }}
                  onError={(event) => {
                    if (showBatBranding) {
                      event.currentTarget.src = isPmiProject ? forloopLogoWhite : batLogoWhite;
                    }
                  }}
                />
              )}

              {/* Forloop logo — always shown, "Powered by" prefix in white-label mode */}
              {isWhiteLabel ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.65)',
                      fontSize: '0.58rem',
                      fontWeight: 400,
                      lineHeight: 1,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Powered by
                  </Typography>
                  <img
                    src={forloopLogoWhite}
                    alt="Forloop Digishelf Insights"
                    style={{ height: 16, display: 'block' }}
                    onError={(event) => { event.currentTarget.src = logo; }}
                  />
                </Box>
              ) : (
                !showBranding && (
                  <img
                    src={logo}
                    alt="Forloop Digishelf Insights"
                    style={{ height: 28 }}
                  />
                )
              )}

            </Box>
          </Link>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {(showMarketSelector || isJtiScopeProject) && (
            <ThemeProvider theme={navFilterTheme}>
              {showMarketSelector && (
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel sx={{ color: '#fff' }}>Market</InputLabel>
                  <Select
                    value={selectedMarket || ''}
                    label="Market"
                    onChange={(event) => onMarketChange && onMarketChange(event.target.value)}
                    sx={{
                      color: '#fff',
                      '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.7)' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#fff' },
                      '.MuiSvgIcon-root': { color: '#fff' },
                    }}
                  >
                    {marketOptions.map((market) => (
                      <MenuItem key={market.id} value={market.id}>
                        {market.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {isJtiScopeProject && (
                <ToggleButtonGroup
                  color="primary"
                  size="small"
                  exclusive
                  value={scopeMode}
                  onChange={(_, value) => {
                    if (value && onScopeChange) {
                      onScopeChange(value);
                    }
                  }}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    borderRadius: 1,
                    '& .MuiToggleButton-root': {
                      color: '#fff',
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 1.5,
                    },
                    '& .MuiToggleButton-root.Mui-selected': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      color: '#fff',
                    },
                  }}
                >
                  <ToggleButton value="hq">HQ</ToggleButton>
                  <ToggleButton value="market">Market</ToggleButton>
                </ToggleButtonGroup>
              )}
            </ThemeProvider>
          )}
          {isLoggedIn && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <NotificationsBell projectKey={projectKey} />
              <ProfileBubble />
            </Box>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
}

export { NavigationBar };